import type { IVariantProduct, IVariantPriceData, IPlatformProductDetail } from '../utils';
import type { ICampaignVariantVersion } from '~/modules/campaign/types';

import {
  toPickPageItemFromVariantPrice,
  toVariantEffectivePriceRange,
  toVariantPriceRange,
  toVariantProductInfo,
} from '../utils';
import { usePlatformProductDetails } from '../hooks/usePlatformProductDetails';
import { findVariantByOrigin, useCampaignContext } from '~/modules/campaign';
import { createContext, useContext, useMemo } from 'react';

interface ICampaignPriceProductContext {
  productByVariantId: Record<string, IVariantProduct>;
  /** Raw fetch result keyed by baseID, not variantId — what callers needing full variants read off. */
  products?: IPlatformProductDetail[];
  isLoadingProducts: boolean;
  /** Re-reads every product on the campaign's variants — what a "sync price" action asks for. */
  handleRefreshProducts: () => Promise<void>;
}

const CampaignPriceProductContext = createContext<ICampaignPriceProductContext | null>(null);

export const useCampaignPriceProductContext = (): ICampaignPriceProductContext => {
  const context = useContext(CampaignPriceProductContext);
  if (!context)
    throw new Error(
      'useCampaignPriceProductContext must be used within a CampaignPriceProductProvider',
    );

  return context;
};

export const CampaignPriceProductProvider: React.FC<{
  children: React.ReactNode;
  campaignVariants?: Record<string, ICampaignVariantVersion>;
}> = ({ children, campaignVariants }) => {
  const { campaign } = useCampaignContext();

  // A version's snapshot wins over the live price: a report must keep showing the product that version
  // actually tested, even after the variant was pointed at another product — or had it removed entirely.
  const priceByVariantId = useMemo(() => {
    const map: Record<string, NonNullable<IVariantPriceData>> = {};

    campaign?.variants?.forEach((variant) => {
      if (!variant) return;

      const versionProduct = campaignVariants?.[variant.id]?.product;
      // The cast in extractVersionSettings hides key typos, so only trust a snapshot that actually
      // carries the one field every consumer keys off. Without baseID nothing downstream can resolve
      // the product anyway, so degrade to the live price instead of a half-built snapshot.
      const price = versionProduct?.baseID
        ? {
            ...versionProduct,
            id: variant.price?.id ?? variant.id,
            // Not mirrored into the version snapshot — it predates this field, and a snapshot is never
            // treated as a duplicate to validate against its Control anyway (see isVersionSnapshot below).
            sourceProductBaseID: '',
          }
        : variant.price;
      if (!price) return;

      map[variant.id] = price;
    });

    return map;
  }, [campaign?.variants, campaignVariants]);

  // Multiple variants (Control, Variant B, ...) can point at the same product, so dedupe by baseID —
  // otherwise a product shared across variants gets fetched once per variant that uses it.
  const baseIDs = useMemo(() => {
    const result = new Set<string>();

    Object.values(priceByVariantId).forEach((price) => {
      if (!price.baseID) return;
      result.add(price.baseID);
    });

    return Array.from(result);
  }, [priceByVariantId]);

  const { products, isLoadingProducts, handleRefreshProducts } = usePlatformProductDetails(baseIDs);

  const controlVariantId = useMemo(() => findVariantByOrigin(campaign, true)?.id, [campaign]);

  // Backs the Variant's effective price range: a saved price only reaches the duplicate once the
  // campaign starts, so reading the duplicate's own Shopify variants would show the Control's prices
  // back at the merchant instead of what was actually saved for the Variant.
  const controlProduct = useMemo(() => {
    const controlBaseID = controlVariantId ? priceByVariantId[controlVariantId]?.baseID : undefined;

    return products?.find(({ baseID }) => baseID === controlBaseID);
  }, [controlVariantId, priceByVariantId, products]);

  const productByVariantId = useMemo(() => {
    const map: Record<string, IVariantProduct> = {};

    Object.entries(priceByVariantId).forEach(([variantId, price]) => {
      const product = products?.find(({ baseID }) => baseID === price.baseID);

      // A version snapshot is a frozen record of what that version actually ran, so its own product's
      // live variants are the right baseline — there is no "saved but not live yet" price to merge in
      // for a version that already ran its course.
      const isVersionSnapshot = !!campaignVariants?.[variantId]?.product?.baseID;
      const isChallenger = variantId !== controlVariantId;
      const productVariants = campaign?.variants?.find(
        (variant) => variant?.id === variantId,
      )?.productVariants;

      const priceRange =
        isChallenger && !isVersionSnapshot
          ? toVariantEffectivePriceRange({
              controlVariants: controlProduct?.variants,
              productVariants,
            })
          : toVariantPriceRange({ variants: product?.variants });

      map[variantId] = {
        ...toPickPageItemFromVariantPrice(
          price,
          product ? toVariantProductInfo(product) : undefined,
        ),
        priceRange,
      };
    });

    return map;
  }, [
    priceByVariantId,
    products,
    campaignVariants,
    campaign?.variants,
    controlVariantId,
    controlProduct,
  ]);

  const value = useMemo<ICampaignPriceProductContext>(
    () => ({
      productByVariantId,
      products,
      isLoadingProducts,
      handleRefreshProducts,
    }),
    [productByVariantId, products, isLoadingProducts, handleRefreshProducts],
  );

  return (
    <CampaignPriceProductContext.Provider value={value}>
      {children}
    </CampaignPriceProductContext.Provider>
  );
};
